import { IMapFeature } from '@/types/features';
import { ITrainersTool } from '@/types/trainers';
import { Frame } from '@/types/location';
import { Order } from '@/hooks/useSortable';
import { IUserWithConfig } from '@/pages/admin/appTargeting';

export const sortByIdx = (a: { idx: number }, b: { idx: number }) => {
  if (a.idx < b.idx) {
    return -1;
  }
  if (a.idx > b.idx) {
    return 1;
  }
  return 0;
};

export const sortObjectByDateProperty = (obj: any) => {
  const sortedArray = Object.entries(obj).sort(([keyA], [keyB]) => {
    const dateA = new Date(keyA).getTime();
    const dateB = new Date(keyB).getTime();
    return dateA - dateB;
  });

  return Object.fromEntries(sortedArray);
};

export const sortByDateCreatedStatusOrClass = (
  orderBy: string,
  order: Order,
) => {
  return (a: IMapFeature, b: IMapFeature) => {
    if (orderBy === 'createdAt') {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();

      if (order === 'asc') {
        return dateA - dateB;
      } else if (order === 'desc') {
        return dateB - dateA;
      }
    }

    if (orderBy === 'auditPassed') {
      const auditA = a.auditPassed ? 1 : 0;
      const auditB = b.auditPassed ? 1 : 0;

      if (order === 'asc') {
        return auditA - auditB;
      } else if (order === 'desc') {
        return auditB - auditA;
      }
    }

    if (orderBy === 'label') {
      if (order === 'asc') {
        if (a.label < b.label) return -1;
        if (a.label > b.label) return 1;
      } else if (order === 'desc') {
        if (a.label < b.label) return 1;
        if (a.label > b.label) return -1;
      }
    }

    return 0;
  };
};

export const sortFramesBySequenceAndTimestamp = (frames: Frame[]) => {
  // First, sort by timestamp and then by sequence
  frames.sort((a, b) => {
    if (a.sequence === b.sequence) {
      return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    }
    return a.sequence.localeCompare(b.sequence);
  });

  // Then, sort the grouped sequences by their latest timestamp
  frames.sort((a, b) => {
    const maxTimestampA = frames
      .filter(frame => frame.sequence === a.sequence)
      .reduce(
        (max, frame) => Math.max(max, new Date(frame.timestamp).getTime()),
        -Infinity,
      );

    const maxTimestampB = frames
      .filter(frame => frame.sequence === b.sequence)
      .reduce(
        (max, frame) => Math.max(max, new Date(frame.timestamp).getTime()),
        -Infinity,
      );

    return maxTimestampB - maxTimestampA;
  });

  return frames;
};

export const sortSequencesByTimestamp = (sequences: Frame[][]): Frame[][] => {
  const nonEmptySequences = sequences.filter(seq => seq.length > 0);

  const sortedSequences = nonEmptySequences.sort((aGroup, bGroup) => {
    const maxTimestampA = aGroup[0]?.timestamp || 0;
    const maxTimestampB = bGroup[0]?.timestamp || 0;

    return (
      new Date(maxTimestampB).getTime() - new Date(maxTimestampA).getTime()
    );
  });

  return sortedSequences;
};

export const getAccessibleAndNonAccessibleTools = (
  accessMap: Record<string, boolean>,
  tools: ITrainersTool[],
) => {
  const accessibleTools: ITrainersTool[] = [];
  const nonAccessibleTools: ITrainersTool[] = [];

  tools.forEach(tool => {
    if (accessMap[tool._id]) {
      accessibleTools.push(tool);
    } else {
      nonAccessibleTools.push(tool);
    }
  });

  return { accessibleTools, nonAccessibleTools };
};

export const sortedTools = (
  tools: ITrainersTool[],
  toolRenderOrder: Record<string, number>,
  toolsToCachedRECounts: Record<string, number>,
): ITrainersTool[] => {
  //Sort tools based on priority coming from Config on Server and if there is no priority, sort based on queue count
  return tools.sort((a, b) => {
    const priorityForA = toolRenderOrder[a._id] ?? 0;
    const priorityForB = toolRenderOrder[b._id] ?? 0;
    const countA = toolsToCachedRECounts[a._id] ?? 0;
    const countB = toolsToCachedRECounts[b._id] ?? 0;

    // If both render orders are non-zero, sort based on render order
    if (priorityForA !== 0 && priorityForB !== 0) {
      return priorityForA - priorityForB;
    }
    // If only one render order is non-zero, it comes first
    else if (priorityForA !== 0) {
      return -1;
    } else if (priorityForB !== 0) {
      return 1;
    }
    // If both render orders are zero or undefined, sort based on count
    else {
      return countB - countA;
    }
  });
};

export const sortByUsername = (users: IUserWithConfig[]) => {
  return users.sort((a: IUserWithConfig, b: IUserWithConfig) => {
    const usernameA = a.user.username.toLowerCase();
    const usernameB = b.user.username.toLowerCase();

    if (usernameA < usernameB) return -1;
    if (usernameA > usernameB) return 1;
    return 0;
  });
};
